import { useState, useEffect, useCallback, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AuthContext } from '@/lib/auth/models/auth.model';
import { MediaItem, normalizeMediaItem } from '@/types/media.types';
import { inclusiveDayCount } from '@/components/utils/dateUtils';

export interface OrphanItinerary {
  id: string;
  title: string;
  /** I template non hanno date: si misurano in giorni. */
  start_date: string | null;
  end_date: string | null;
  duration_days: number;
  budget: number;
  description: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  /** Cover come MediaItem, stessa forma degli elementi di `media` delle attività. */
  image: MediaItem | null;
  currency: string | null;
  total: number | null;
}

export interface CreateOrphanItineraryPayload {
  title: string;
  /** Durata del template in giorni (il builder li mostra come "Giorno 1..N"). */
  duration_days: number;
  budget: number;
  description?: string;
  image?: MediaItem | null;
}

/**
 * Come risolvere la differenza fra i giorni del template e quelli della richiesta.
 * `keep` tiene la durata del template, `truncate` la taglia su quella della richiesta,
 * `extend` la allunga fino a coprirla.
 */
export type LinkTemplateMode = 'keep' | 'truncate' | 'extend';

export const useOrphanItineraries = () => {
  const [orphanItineraries, setOrphanItineraries] = useState<OrphanItinerary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useOrphanItineraries must be used within an AuthProvider');
  }

  const { user } = authContext;

  // Fetch orphan itineraries accessible to the current user (RLS policies handle filtering)
  const fetchOrphanItineraries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        setOrphanItineraries([]);
        setLoading(false);
        return;
      }

      // Query orphan itineraries - RLS policies will filter based on user role:
      // - Admins: see all
      // - Creators: see their own orphan itineraries
      // - Agency users: see orphans created by themselves and their members (via RLS)
      const { data, error: fetchError } = await supabase
        .from('itineraries')
        .select('*')
        .is('travel_request_id', null)
        .order('created_at', { ascending: false });

      if (fetchError) {
        const errorMsg = t('playground.loadError');
        setError(errorMsg);
        toast({
          title: t('common.error'),
          description: errorMsg,
          variant: 'destructive',
        });
        return;
      }

      // La cover arriva come JSONB grezzo: normalizzala in MediaItem (o null),
      // tollerando le righe legacy in cui era un URL nudo.
      //
      // La durata è la misura di un template: se manca (righe salvate prima del
      // passaggio ai giorni relativi) si ricava dalle date, così chi legge la
      // normalizza in un posto solo invece che a ogni schermata.
      setOrphanItineraries(
        (data || []).map((row: Record<string, unknown>) => ({
          ...row,
          image: normalizeMediaItem(row.image),
          duration_days:
            (row.duration_days as number | null) ??
            (row.start_date && row.end_date
              ? inclusiveDayCount(row.start_date as string, row.end_date as string)
              : 1),
        })) as unknown as OrphanItinerary[]
      );
    } catch (err) {
      const errorMsg = t('errors.generic');
      setError(errorMsg);
      toast({
        title: t('common.error'),
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Create a new orphan itinerary
  const createOrphanItinerary = useCallback(async (payload: CreateOrphanItineraryPayload) => {
    try {
      if (!user?.id) {
        toast({
          title: t('common.error'),
          description: t('auth.loginRequired'),
          variant: 'destructive',
        });
        return null;
      }

      const itineraryData = {
        title: payload.title,
        // Nessuna data: le acquisirà quando verrà collegato a una travel request.
        start_date: null,
        end_date: null,
        duration_days: payload.duration_days,
        budget: payload.budget,
        description: payload.description || '',
        travel_request_id: null,
        status: 'draft',
        image: payload.image ?? null,
        guests_data: [],
        // Il totale lo calcolano le attività: un template appena creato è vuoto.
        total: 0,
      };

      // Use save_itinerary RPC (SECURITY DEFINER) to bypass RLS on direct INSERT,
      // which can fail for travel_advisor_member due to auth.uid() resolution issues.
      const { data, error: insertError } = await supabase
        .rpc('save_itinerary', { p_itinerary: itineraryData });

      if (insertError) {
        toast({
          title: t('common.error'),
          description: t('playground.createError'),
          variant: 'destructive',
        });
        return null;
      }

      toast({
        variant: "success",
        title: t("common.success"),
        description: t("playground.createSuccess"),
      });

      // Refresh the list
      await fetchOrphanItineraries();

      return data;
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: 'destructive',
      });
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Link an orphan itinerary to a travel request (duplicate and link)
  const linkItineraryToTravelRequest = useCallback(async (
    itineraryId: string,
    travelRequestId: string,
    options?: { mode?: LinkTemplateMode; startDayOffset?: number }
  ) => {
    try {
      // La RPC converte i giorni relativi del template in date reali a partire
      // dall'inizio della richiesta (più l'eventuale offset scelto dall'utente).
      const { data, error: rpcError } = await supabase.rpc('link_template_to_travel_request' as any, {
        p_source_itinerary_id: itineraryId,
        p_target_travel_request_id: travelRequestId,
        p_start_day_offset: options?.startDayOffset ?? 0,
        p_mode: options?.mode ?? 'keep',
      });

      if (rpcError) {
        toast({
          title: t('common.error'),
          description: t('playground.linkError'),
          variant: 'destructive',
        });
        return null;
      }

      toast({ variant: 'success',
        title: t('common.success'),
        description: t('playground.linkSuccess'),
      });

      return data;
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: 'destructive',
      });
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Delete an orphan itinerary
  const deleteOrphanItinerary = useCallback(async (itineraryId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('itineraries')
        .delete()
        .eq('id', itineraryId);

      if (deleteError) {
        toast({
          title: t('common.error'),
          description: t('playground.deleteError'),
          variant: 'destructive',
        });
        return false;
      }

      toast({ variant: 'success',
        title: t('common.success'),
        description: t('playground.deleteSuccess'),
      });

      // Refresh the list
      await fetchOrphanItineraries();

      return true;
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: 'destructive',
      });
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fetch on mount and when user changes
  // Auto-fetch on mount
  useEffect(() => {
    fetchOrphanItineraries();
  }, [fetchOrphanItineraries]);

  return {
    orphanItineraries,
    loading,
    error,
    refreshData: fetchOrphanItineraries,
    createOrphanItinerary,
    linkItineraryToTravelRequest,
    deleteOrphanItinerary,
  };
};
